import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FilterX } from "lucide-react";
import { useMemo, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  useUtilityServices,
  useGetManufacturers,
  useChoices,
  useGetMeterModel,
  useGetPremise,
  useGetSubArea,
} from "@features/mx/device-management/hooks";
import { useMeterReadingStatus } from "@features/bx/singleBillGenaration/hooks";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useGetArea } from "@features/mx/readCycleManagement/hooks";

export interface MeterFiltersType {
  meterType?: string; // ✅ Keep camelCase
  service?: string;
  manufacturer?: string;
  model?: string;
  area?: string;
  subarea?: string;
  premise?: string;
  readingStatus?: string; // ✅ Keep camelCase
  dateFrom?: string; // ✅ Keep camelCase
  dateTo?: string; // ✅ Keep camelCase
}
interface MeterFiltersProps {
  filters: MeterFiltersType;
  onApplyFilters: (filters: MeterFiltersType) => void;
  onClearFilters: () => void;
  remoteUtilityId: string;
  operationType: number | null;
  isLoading?: boolean;
}

export const MeterFilters = ({
  filters,
  onApplyFilters,
  onClearFilters,
  remoteUtilityId,
  operationType,
  isLoading = false,
}: MeterFiltersProps) => {
  // Fetch data from hooks
const [selectedArea, setSelectedArea] = useState(filters.area || "");
const [selectedSubarea, setSelectedSubarea] = useState(filters.subarea || "");
  const { data: utilityServices } = useUtilityServices(remoteUtilityId);
  const { data: manufacturers } = useGetManufacturers();
  const { data: meterTypes } = useChoices({ choice: "meter_type" });
  const { data: meterReadingStatus } = useMeterReadingStatus();

const { data: areaData } = useGetArea();
const { data: subAreaData } = useGetSubArea(selectedArea); 
const { data: premisesData } = useGetPremise(selectedSubarea);
  // Watch form values for dependent fields
  const [meterType, setMeterType] = useState(filters.meterType || "");
  const [meterManufacturer, setMeterManufacturer] = useState(
    filters.manufacturer || ""
  );



  // Fetch meter models only when both meterType and manufacturer are selected
  const { data: meterModels } = useGetMeterModel(meterType, meterManufacturer);

  // Transform data to options
  const utilityOptions = useMemo(() => {
    if (!utilityServices || !Array.isArray(utilityServices)) return [];
    return utilityServices
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilityServices]);

  const manufacturerOptions = useMemo(() => {
    if (!manufacturers || !Array.isArray(manufacturers)) return [];
    return manufacturers.map((manufacturer) => ({
      value: String(manufacturer.id),
      label: manufacturer.name,
    }));
  }, [manufacturers]);

  const meterTypeOptions = useMemo(() => {
    if (!meterTypes || !Array.isArray(meterTypes)) return [];
    return meterTypes.map((type) => ({
      value: String(type.key),
      label: type.value,
    }));
  }, [meterTypes]);

  const meterModelOptions = useMemo(() => {
    if (!meterModels || !Array.isArray(meterModels)) return [];
    return meterModels.map((model) => ({
      value: String(model.id),
      label: model.meterModel,
    }));
  }, [meterModels]);

  const readingStatusOptions = useMemo(() => {
    if (!meterReadingStatus || !Array.isArray(meterReadingStatus)) return [];
    return meterReadingStatus.map((status) => ({
      value: String(status.key),
      label: status.value,
    }));
}, [meterReadingStatus]);

const areaOptions = useMemo(() => {
  if (!areaData || !Array.isArray(areaData)) return [];
  return areaData.map((area) => ({
    value: String(area.code),
    label: area.name,
  }));
}, [areaData]);

const subAreaOptions = useMemo(() => {
  if (!subAreaData || !Array.isArray(subAreaData)) return [];
  return subAreaData.map((subArea) => ({
    value: String(subArea.code),
    label: subArea.name,
  }));
}, [subAreaData]);

const premiseOptions = useMemo(() => {
  if (!Array.isArray(premisesData)) return [];
  return premisesData.map((premise) => ({
    value: String(premise.code),
    label: premise.name,
  }));
}, [premisesData]);

  // Define filter fields based on operation type
// Define filter fields based on operation type
const getFilterFields = (): FormField[] => {
  const baseFields: FormField[] = [];

  // Common fields with camelCase names
  const commonFields = {
    manufacturer: {
      name: "manufacturer",
      type: "select" as const,
      label: "Manufacturer",
      placeholder: "Select manufacturer",
      options: manufacturerOptions,
      classes: FIELD_CLASSES,
    },
    meterType: {
      name: "meterType", // ✅ camelCase
      type: "select" as const,
      label: "Meter Type",
      placeholder: "Select meter type",
      options: meterTypeOptions,
      classes: FIELD_CLASSES,
    },
    model: {
      name: "model",
      type: "select" as const,
      label: "Model",
      placeholder: "Select model",
      options: meterModelOptions,
      disabled: !meterType || !meterManufacturer,
      classes: FIELD_CLASSES,
    },
    service: {
      name: "service",
      type: "select" as const,
      label: "Utility Service",
      placeholder: "Select service",
      options: utilityOptions,
      classes: FIELD_CLASSES,
    },
    area: {
      name: "area",
      type: "select" as const,
      label: "Area",
      placeholder: "Select area",
      options: areaOptions,
      disabled: areaOptions.length === 0,
      classes: FIELD_CLASSES,
    },
    subarea: {
      name: "subarea",
      type: "select" as const,
      label: "Subarea",
      placeholder: selectedArea ? "Select subarea" : "Select area first", // ✅ Dynamic placeholder
      options: subAreaOptions,
      disabled: !selectedArea || subAreaOptions.length === 0, // ✅ Disabled until area is selected
      classes: FIELD_CLASSES,
    },
    premise: {
      name: "premise",
      type: "select" as const,
      label: "Premise",
      placeholder: selectedSubarea ? "Select premise" : "Select subarea first", // ✅ Dynamic placeholder
      options: premiseOptions,
      disabled: !selectedSubarea || premiseOptions.length === 0, // ✅ Disabled until subarea is selected
      classes: FIELD_CLASSES,
    },
    readingStatus: {
      name: "readingStatus", // ✅ camelCase
      type: "select" as const,
      label: "Reading Status",
      placeholder: "Select reading status",
      options: readingStatusOptions,
      classes: FIELD_CLASSES,
    },
    dateFrom: {
      name: "dateFrom", // ✅ camelCase
      type: "datepicker" as const,
      label: "Date From",
      placeholder: "Select start date",
      classes: {
        container: "w-full",
        label: "block text-sm font-medium mb-1",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
      },
    },
    dateTo: {
      name: "dateTo", // ✅ camelCase
      type: "datepicker" as const,
      label: "Date To",
      placeholder: "Select end date",
      classes: {
        container: "w-full",
        label: "block text-sm font-medium mb-1",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
      },
    },
  };

  // Operation type 7: Meter Installation
  if (operationType === 7) {
    return [
      commonFields.manufacturer,
      commonFields.meterType,
      commonFields.model,
      commonFields.area,
    ];
  }

  // Operation type 17: Meter Disposal
  if (operationType === 17) {
    return [
      commonFields.meterType,
      commonFields.service,
      commonFields.manufacturer,
      commonFields.model,
      commonFields.dateFrom,
      commonFields.dateTo,
    ];
  }

  // Operation type 1: Meter Replacement
  if (operationType === 1) {
    return [
      commonFields.service,
      commonFields.meterType,
      commonFields.manufacturer,
      commonFields.model,
      commonFields.area,
      commonFields.subarea,
      commonFields.premise,
      commonFields.readingStatus,
    ];
  }

  // Default: show all fields
  return Object.values(commonFields);
};
  const filterFields = getFilterFields();

  // Create form instance
  const form: UseFormReturn<MeterFiltersType> =
    FormService.createForm<MeterFiltersType>({
      fields: filterFields,
      defaultValues: filters,
    });

  // Watch for changes in meterType and manufacturer
useEffect(() => {
  const subscription = form.watch((value, { name }) => {
    if (name === "meterType") { // ✅ camelCase
      setMeterType(value.meterType || "");
      form.setValue("model", "");
    }
    if (name === "manufacturer") {
      setMeterManufacturer(value.manufacturer || "");
      form.setValue("model", "");
    }
     // ✅ Handle area change
    if (name === "area") {
      const newArea = value.area || "";
      setSelectedArea(newArea);
      
      // Reset subarea and premise when area changes
      if (newArea !== selectedArea) {
        form.setValue("subarea", "");
        form.setValue("premise", "");
        setSelectedSubarea("");
      }
    }

    // ✅ Handle subarea change
    if (name === "subarea") {
      const newSubarea = value.subarea || "";
      setSelectedSubarea(newSubarea);
      
      // Reset premise when subarea changes
      if (newSubarea !== selectedSubarea) {
        form.setValue("premise", "");
      }
    }
  });
  return () => subscription.unsubscribe();
}, [form]);

  // Reset form when operation type changes
  useEffect(() => {
    form.reset({});
    setMeterType("");
    setMeterManufacturer("");
  }, [operationType]);

  // Handle form submission
  const handleSubmit = (data: MeterFiltersType) => {
    // Remove empty values
    const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
      if (value && value !== "" && value !== "all") {
        acc[key as keyof MeterFiltersType] = value;
      }
      return acc;
    }, {} as MeterFiltersType);

    onApplyFilters(cleanedData);
  };

  // Handle reset
  const handleReset = () => {
    form.reset({});
    setMeterType("");
    setMeterManufacturer("");
    onClearFilters();
  };

  // Custom footer
  const renderCustomFooter = () => (
    <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
      <Button
        type="button"
        variant="ghost"
        onClick={handleReset}
        disabled={isLoading}
      >
        <FilterX className="w-4 h-4 mr-1" />
        Clear Filters
      </Button>
      <Button type="submit" disabled={isLoading}>
        Apply Filters
      </Button>
    </div>
  );

  if (!operationType) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Please select an operation type first
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={operationType === 1 ? 4 : operationType === 17 ? 3 : 2}
        responsiveColumns={{
          sm: 1,
          md: 2,
          lg: operationType === 1 ? 4 : operationType === 17 ? 3 : 2,
        }}
        
        className="space-y-4"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
